import React, { useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface BrandingPreviewProps {
  branding: any;
  /**
   * Optional draft content (markdown). When provided, the preview renders THIS
   * document's real text with the branding applied - as a single continuous
   * sheet that grows to fit - instead of the two hardcoded sample pages.
   */
  content?: string;
}

/** Strip the inline markdown a heading/paragraph preview doesn't render. */
const stripInline = (s: string): string =>
  s
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\((.+?)\)/g, '$1')
    .trim();

type Block = { level: 'H1' | 'H2' | 'H3' | 'H4' | 'Body'; text: string };

/** HTML → blocks (the editor can persist content as HTML rather than markdown). */
const htmlToBlocks = (html: string): Block[] => {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const out: Block[] = [];
    const walk = (nodes: NodeListOf<ChildNode> | ChildNode[]) => {
      nodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();
        const text = (el.textContent || '').trim();
        if (!text) return;
        if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4') {
          out.push({ level: tag.toUpperCase() as Block['level'], text });
        } else if (tag === 'p' || tag === 'li' || tag === 'blockquote') {
          out.push({ level: 'Body', text });
        } else if (['div', 'section', 'article', 'ul', 'ol', 'body'].includes(tag)) {
          walk(el.childNodes);
        } else {
          out.push({ level: 'Body', text });
        }
      });
    };
    walk(doc.body.childNodes);
    return out;
  } catch {
    return [{ level: 'Body', text: stripInline(html) }];
  }
};

/** Lightweight markdown → heading/paragraph blocks for the live preview. */
const parseBlocks = (md: string): Block[] => {
  // Detect HTML content and route it through the DOM parser instead.
  if (/<\/?(p|h[1-4]|div|ul|ol|li|br|blockquote)\b/i.test(md || '')) {
    return htmlToBlocks(md);
  }
  const lines = (md || '').replace(/\r/g, '').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) {
      blocks.push({ level: 'Body', text: stripInline(para.join(' ')) });
      para = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      flush();
      const lvl = (['H1', 'H2', 'H3', 'H4'] as const)[h[1].length - 1];
      blocks.push({ level: lvl, text: stripInline(h[2]) });
      continue;
    }
    // A short **bold-only** line reads as a heading in legal drafts.
    const b = /^\*\*(.+?)\*\*:?$/.exec(line);
    if (b && line.length < 120) {
      flush();
      blocks.push({ level: 'H2', text: stripInline(b[1]) });
      continue;
    }
    if (!line) { flush(); continue; }
    para.push(line);
  }
  flush();
  return blocks;
};

// Page sizes at 96 DPI (CSS px standard)
const DPI = 96;
const PAGE_SIZES_IN: Record<string, { w: number; h: number }> = {
  A4: { w: 8.27, h: 11.69 },
  Letter: { w: 8.5, h: 11 },
};

const DEFAULTS = {
  Enabled: false,
  PaperSize: 'A4' as 'A4' | 'Letter',
  FirstPageDifferent: false,
  Typography: {
    FontType: 'Times New Roman',
    DocumentFontSize: 12,
    HeadingFontSize: 'auto',
    LineSpacing: 1.5,
    ParagraphSpacingBefore: 0,
    ParagraphSpacingAfter: 6,
    Alignment: 'left' as 'left' | 'justify' | 'right' | 'center',
    Levels: { H1: {}, H2: {}, H3: {}, H4: {}, Body: {} },
  },
  Margins: { Top: 1, Bottom: 1, Left: 1, Right: 1 },
  TableOfContents: {
    Enabled: false,
    Depth: 3,
    Style: 'dotted',
  },
  PageNumber: {
    Enabled: false,
    Format: 'Page X of Y',
    Position: 'footer-center',
    StartAt: 1,
  },
  Letterhead: {
    Enabled: false,
    FirmName: '',
    AddressLines: '',
    OrgNumber: '',
    Contact: '',
  },
  Confidentiality: {
    Enabled: false,
    Text: '',
    Placement: 'first-page',
  },
  Logos: {
    UseFirmLogo: false,
    FirmLogo: null,
    FirmLogoSize: 24,
    Color: true,
    UseWatermark: false,
    WatermarkLogo: null,
    WatermarkText: '',
    WatermarkOpacity: 25,
  },
  HeaderFooter: {
    HeaderMode: 'text',
    FooterMode: 'text',
    // Per-position modes
    HeaderLeftMode: 'none' as 'none' | 'text' | 'logo',
    HeaderCenterMode: 'none' as 'none' | 'text' | 'logo',
    HeaderRightMode: 'none' as 'none' | 'text' | 'logo',
    FooterLeftMode: 'none' as 'none' | 'text' | 'logo',
    FooterCenterMode: 'none' as 'none' | 'text' | 'logo',
    FooterRightMode: 'text' as 'none' | 'text' | 'logo',
    // Per-position images
    HeaderLeftImage: null as string | File | null,
    HeaderCenterImage: null as string | File | null,
    HeaderRightImage: null as string | File | null,
    FooterLeftImage: null as string | File | null,
    FooterCenterImage: null as string | File | null,
    FooterRightImage: null as string | File | null,
    HeaderTextLeft: '',
    HeaderTextCenter: '',
    HeaderTextRight: '',
    FooterTextLeft: '',
    FooterTextCenter: '',
    FooterTextRight: 'Page {page}',
    HeaderFontType: '',
    FooterFontType: '',
    HeaderFontSize: 10,
    FooterFontSize: 10,
    HeaderLogoSize: 20,
    FooterLogoSize: 18,
  },
};

const sizeLadderPt: Record<'H1' | 'H2' | 'H3' | 'H4', number> = {
  H1: 18, H2: 14, H3: 12, H4: 11,
};

/**
 * Map non-web-safe fonts to CSS font-family stacks with proper fallbacks.
 * Aptos / Aptos Display are Microsoft 365 proprietary fonts - they won't
 * render in browsers unless the user has them installed locally, so we
 * provide the closest web-safe fallback in the stack.
 */
const FONT_FALLBACK_MAP: Record<string, string> = {
  'Aptos':            '"Aptos", "Calibri", "Helvetica Neue", Helvetica, Arial, sans-serif',
  'Aptos Display':    '"Aptos Display", "Cambria", "Georgia", "Times New Roman", serif',
  'Garamond':         '"EB Garamond", "Garamond", "Georgia", serif',
  'Calibri':          '"Calibri", "Helvetica Neue", Helvetica, Arial, sans-serif',
  'Cambria':          '"Cambria", "Georgia", "Times New Roman", serif',
  'Century Schoolbook':'"Century Schoolbook", "Georgia", serif',
  'Palatino Linotype':'"Palatino Linotype", "Palatino", "Book Antiqua", "Georgia", serif',
  'Book Antiqua':     '"Book Antiqua", "Palatino Linotype", "Georgia", serif',
  'Franklin Gothic':  '"Franklin Gothic Medium", "Arial", sans-serif',
  'Trebuchet MS':     '"Trebuchet MS", "Helvetica Neue", Arial, sans-serif',
};

const resolveFontFamily = (fontName: string | undefined, fallbackFont?: string): string => {
  if (!fontName) return fallbackFont ? resolveFontFamily(fallbackFont) : '"Times New Roman", Times, serif';
  // Check the fallback map first (case-insensitive match)
  const mapped = FONT_FALLBACK_MAP[fontName] || 
    Object.entries(FONT_FALLBACK_MAP).find(([k]) => k.toLowerCase() === fontName.toLowerCase())?.[1];
  if (mapped) return mapped;
  // Generic: wrap in quotes and add a generic family
  const lc = fontName.toLowerCase();
  const isSerif = ['times', 'georgia', 'garamond', 'palatino', 'cambria', 'baskerville', 'bookman', 'century'].some(s => lc.includes(s));
  const generic = isSerif ? 'serif' : 'sans-serif';
  return `"${fontName}", ${generic}`;
};

const BrandingPreview: React.FC<BrandingPreviewProps> = ({ branding, content }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.6);
  // In content mode we paginate the real content across fixed-height sheets by
  // measuring where each block lands. null until measured.
  const [pageSlices, setPageSlices] = useState<number[][] | null>(null);

  const contentMode = !!(content && content.trim());
  const blocks = useMemo(() => (contentMode ? parseBlocks(content as string) : []), [contentMode, content]);

  const b = useMemo(() => {
    if (!branding || !branding.Enabled) return DEFAULTS;
    return {
      ...DEFAULTS,
      ...branding,
      Typography: {
        ...DEFAULTS.Typography,
        ...(branding.Typography || {}),
        Levels: { ...DEFAULTS.Typography.Levels, ...(branding.Typography?.Levels || {}) },
      },
      Margins: { ...DEFAULTS.Margins, ...(branding.Margins || {}) },
      TableOfContents: { ...DEFAULTS.TableOfContents, ...(branding.TableOfContents || {}) },
      Logos: { ...DEFAULTS.Logos, ...(branding.Logos || {}) },
      HeaderFooter: { ...DEFAULTS.HeaderFooter, ...(branding.HeaderFooter || {}) },
      PageNumber: { ...DEFAULTS.PageNumber, ...(branding.PageNumber || {}) },
      Letterhead: { ...DEFAULTS.Letterhead, ...(branding.Letterhead || {}) },
      Confidentiality: { ...DEFAULTS.Confidentiality, ...(branding.Confidentiality || {}) },
    };
  }, [branding]);

  // Dynamic page dimensions based on selected paper size.
  const pageDims = PAGE_SIZES_IN[b.PaperSize as string] || PAGE_SIZES_IN.A4;
  const PAGE_WIDTH = pageDims.w * DPI;
  const PAGE_HEIGHT = pageDims.h * DPI;

  // Responsive: measure container width and compute scale (recomputes on paper-size change)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const compute = (availableWidth: number) => {
      const newScale = Math.min((availableWidth - 16) / PAGE_WIDTH, 1);
      setScale(Math.max(newScale, 0.3));
    };
    compute(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) compute(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [PAGE_WIDTH]);

  // Content mode: paginate the content across sheets. Measure each block's
  // position in a hidden full-flow container (at the true content width) and
  // greedily break to a new page when the next block would overflow the page's
  // content height. Gives a real "Page X of Y" preview of the whole document.
  useLayoutEffect(() => {
    if (!contentMode) { setPageSlices(null); return; }
    const el = measureRef.current;
    if (!el) return;
    const compute = () => {
      const kids = Array.from(el.children) as HTMLElement[];
      if (!kids.length) { setPageSlices([[]]); return; }
      const usable = Math.max(
        120,
        PAGE_HEIGHT - ((b.Margins?.Top ?? 1) + (b.Margins?.Bottom ?? 1)) * DPI,
      );
      const slices: number[][] = [];
      let cur: number[] = [];
      let pageTop = kids[0].offsetTop;
      kids.forEach((k, i) => {
        const bottom = k.offsetTop + k.offsetHeight;
        if (cur.length && (bottom - pageTop) > usable) {
          slices.push(cur); cur = []; pageTop = k.offsetTop;
        }
        cur.push(i);
      });
      if (cur.length) slices.push(cur);
      setPageSlices(slices.length ? slices : [[]]);
    };
    compute();
    const ro = new ResizeObserver(compute); ro.observe(el);
    return () => ro.disconnect();
  }, [contentMode, content, b, PAGE_HEIGHT]);

  // Convert inches to pixels at base scale (applied at the page level via CSS transform)
  const inToPx = (inches: number) => inches * DPI;
  
  // Convert points to pixels (1pt = 1/72 inch)
  const ptToPx = (pt: number) => (pt / 72) * DPI;

  const firmLogoUrl = useMemo(() => {
    const src = b.Logos?.FirmLogo;
    if (typeof src === 'string') return src;
    if (src instanceof File) return URL.createObjectURL(src);
    return '';
  }, [b.Logos?.FirmLogo]);

  const watermarkLogoUrl = useMemo(() => {
    const src = b.Logos?.WatermarkLogo;
    if (typeof src === 'string') return src;
    if (src instanceof File) return URL.createObjectURL(src);
    return '';
  }, [b.Logos?.WatermarkLogo]);

  // Helper to get URL from string or File
  const getImageUrl = (src: string | File | null | undefined): string => {
    if (!src) return '';
    if (typeof src === 'string') return src;
    if (src instanceof File) return URL.createObjectURL(src);
    return '';
  };

  // Per-position image URLs
  const headerLeftImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.HeaderLeftImage), [b.HeaderFooter?.HeaderLeftImage]);
  const headerCenterImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.HeaderCenterImage), [b.HeaderFooter?.HeaderCenterImage]);
  const headerRightImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.HeaderRightImage), [b.HeaderFooter?.HeaderRightImage]);
  const footerLeftImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.FooterLeftImage), [b.HeaderFooter?.FooterLeftImage]);
  const footerCenterImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.FooterCenterImage), [b.HeaderFooter?.FooterCenterImage]);
  const footerRightImageUrl = useMemo(() => getImageUrl(b.HeaderFooter?.FooterRightImage), [b.HeaderFooter?.FooterRightImage]);

  // Calculate content area dimensions based on margins
  const marginTop = inToPx(b.Margins?.Top ?? 1);
  const marginBottom = inToPx(b.Margins?.Bottom ?? 1);
  const marginLeft = inToPx(b.Margins?.Left ?? 1);
  const marginRight = inToPx(b.Margins?.Right ?? 1);
  const contentWidth = PAGE_WIDTH - marginLeft - marginRight;
  const contentHeight = PAGE_HEIGHT - marginTop - marginBottom;

  // Get font size in pixels for a level
  const getFontSizePx = (level: 'H1' | 'H2' | 'H3' | 'H4' | 'Body'): number => {
    const lvl = b.Typography?.Levels?.[level] || {};
    let sizePt: number;

    if (typeof lvl.Size === 'number') {
      sizePt = lvl.Size;
    } else if (typeof lvl.Size === 'string' && lvl.Size.toLowerCase() !== 'auto') {
      const n = Number(lvl.Size);
      sizePt = isNaN(n) ? (level === 'Body' ? 12 : sizeLadderPt[level as keyof typeof sizeLadderPt]) : n;
    } else {
      sizePt = level === 'Body' 
        ? Number(b.Typography?.DocumentFontSize) || 12 
        : sizeLadderPt[level as keyof typeof sizeLadderPt] || 12;
    }
    return ptToPx(sizePt);
  };

  const getLevelStyle = (level: 'H1' | 'H2' | 'H3' | 'H4' | 'Body'): React.CSSProperties => {
    const lvl = b.Typography?.Levels?.[level] || {};
    const fontFamily = resolveFontFamily(lvl.FontType, b.Typography?.FontType);
    const fontSize = getFontSizePx(level);
    const fontWeight = level === 'Body' ? 'normal' : 'bold';
    const color = lvl.Color || (level === 'Body' ? '#000' : '#000');
    const bodyLineHeight = Number(b.Typography?.LineSpacing) || 1.5;

    return {
      fontFamily,
      fontSize: `${fontSize}px`,
      fontWeight,
      color,
      lineHeight: level === 'Body' ? bodyLineHeight : 1.4,
      textAlign: level === 'Body'
        ? ((b.Typography?.Alignment as React.CSSProperties['textAlign']) || 'left')
        : undefined,
      margin: 0,
    };
  };

  const getHeaderStyle = (): React.CSSProperties => ({
    fontFamily: resolveFontFamily(b.HeaderFooter?.HeaderFontType, b.Typography?.FontType),
    fontSize: `${ptToPx(b.HeaderFooter?.HeaderFontSize || 10)}px`,
    color: '#666',
  });

  const getFooterStyle = (): React.CSSProperties => ({
    fontFamily: resolveFontFamily(b.HeaderFooter?.FooterFontType, b.Typography?.FontType),
    fontSize: `${ptToPx(b.HeaderFooter?.FooterFontSize || 10)}px`,
    color: '#666',
  });

  const grayscale = b.Logos?.Color === false ? 'grayscale(100%)' : 'none';

  // Document Page Component
  const DocumentPage: React.FC<{ pageNum: number; children: React.ReactNode; showGuides?: boolean; totalPages?: number }> = ({ pageNum, children, showGuides = true, totalPages }) => {
    // Per-position modes (fallback to legacy global modes for backwards compatibility)
    const headerLeftMode = b.HeaderFooter?.HeaderLeftMode || 'none';
    const headerCenterMode = b.HeaderFooter?.HeaderCenterMode || 'none';
    const headerRightMode = b.HeaderFooter?.HeaderRightMode || 'none';
    const footerLeftMode = b.HeaderFooter?.FooterLeftMode || 'none';
    const footerCenterMode = b.HeaderFooter?.FooterCenterMode || 'none';
    const footerRightMode = b.HeaderFooter?.FooterRightMode || 'text';

    // Get three-position text values with {page} placeholder replacement
    const headerTextLeft = b.HeaderFooter?.HeaderTextLeft || '';
    const headerTextCenter = b.HeaderFooter?.HeaderTextCenter || '';
    const headerTextRight = b.HeaderFooter?.HeaderTextRight || '';
    const footerTextLeft = (b.HeaderFooter?.FooterTextLeft || '').replace('{page}', String(pageNum));
    const footerTextCenter = (b.HeaderFooter?.FooterTextCenter || '').replace('{page}', String(pageNum));
    const footerTextRight = (b.HeaderFooter?.FooterTextRight || 'Page {page}').replace('{page}', String(pageNum));
    
    // Check if any position has content
    const hasHeaderContent = headerLeftMode !== 'none' || headerCenterMode !== 'none' || headerRightMode !== 'none';
    const hasFooterContent = footerLeftMode !== 'none' || footerCenterMode !== 'none' || footerRightMode !== 'none';
    
    // Helper to get image URL for a position, fallback to firm logo
    const getPositionImageUrl = (positionImageUrl: string): string => {
      if (positionImageUrl) return positionImageUrl;
      if (b.Logos?.UseFirmLogo && firmLogoUrl) return firmLogoUrl;
      return '';
    };

    // Page numbering (mirrors the live Word fields inserted by the engine)
    const TOTAL_PAGES = totalPages ?? 2; // real page count in content mode
    const pageNumEnabled = !!b.PageNumber?.Enabled;
    const displayNum = (Number(b.PageNumber?.StartAt ?? 1)) + (pageNum - 1);
    const pageNumberText = (() => {
      switch (b.PageNumber?.Format) {
        case 'X': return `${displayNum}`;
        case 'X of Y': return `${displayNum} of ${TOTAL_PAGES}`;
        case 'Page X': return `Page ${displayNum}`;
        case 'none': return '';
        default: return `Page ${displayNum} of ${TOTAL_PAGES}`;
      }
    })();
    const pnPos = (b.PageNumber?.Position || 'footer-center') as string;
    const pnInHeader = pnPos.startsWith('header');
    const pnJustify = pnPos.endsWith('left') ? 'flex-start' : pnPos.endsWith('right') ? 'flex-end' : 'center';

    // Confidentiality notice placement
    const confEnabled = !!b.Confidentiality?.Enabled && !!(b.Confidentiality?.Text || '').trim();
    const confPlacement = b.Confidentiality?.Placement || 'first-page';
    const confText = b.Confidentiality?.Text || '';

    // Letterhead (first page only)
    const lhEnabled = !!b.Letterhead?.Enabled && pageNum === 1;
    const lhAddressLines = String(b.Letterhead?.AddressLines || '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    
    // Margin guide styles - subtle gray/black
    const guideLineStyle: React.CSSProperties = {
      position: 'absolute',
      borderColor: '#9ca3af',
      borderStyle: 'dashed',
      opacity: 0.4,
      pointerEvents: 'none',
    };
    
    const marginLabelStyle: React.CSSProperties = {
      position: 'absolute',
      fontSize: '7px',
      color: '#6b7280',
      fontFamily: 'system-ui, sans-serif',
      fontWeight: 500,
      opacity: 0.7,
      pointerEvents: 'none',
    };

    return (
      <div
        className="relative flex-shrink-0"
        style={{
          width: `${PAGE_WIDTH * scale}px`,
          height: `${PAGE_HEIGHT * scale}px`,
        }}
      >
        <div
          className="absolute bg-white origin-top-left"
          style={{
            width: `${PAGE_WIDTH}px`,
            height: `${PAGE_HEIGHT}px`,
            transform: `scale(${scale})`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
          }}
        >
        {/* Watermark */}
        {b.Logos?.UseWatermark && (
          <div 
            className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
            style={{ opacity: (b.Logos?.WatermarkOpacity ?? 25) / 100 }}
          >
            {watermarkLogoUrl ? (
              <img 
                src={watermarkLogoUrl} 
                alt={t('Watermark')} 
                className="max-w-[60%] max-h-[60%] object-contain"
                style={{ filter: grayscale }}
              />
            ) : (
              <span 
                className="font-medium -rotate-45 select-none whitespace-nowrap"
                style={{ 
                  fontFamily: resolveFontFamily(b.Typography?.FontType),
                  fontSize: `${ptToPx(72)}px`,
                  color: '#ccc',
                }}
              >
                {b.Logos?.WatermarkText || 'DRAFT'}
              </span>
            )}
          </div>
        )}

        {/* Margin Guides */}
        {showGuides && (
          <>
            {/* Top margin line */}
            <div style={{ ...guideLineStyle, top: marginTop, left: 0, right: 0, borderTopWidth: 1 }} />
            {/* Bottom margin line */}
            <div style={{ ...guideLineStyle, bottom: marginBottom, left: 0, right: 0, borderTopWidth: 1 }} />
            {/* Left margin line */}
            <div style={{ ...guideLineStyle, left: marginLeft, top: 0, bottom: 0, borderLeftWidth: 1 }} />
            {/* Right margin line */}
            <div style={{ ...guideLineStyle, right: marginRight, top: 0, bottom: 0, borderLeftWidth: 1 }} />
            
            {/* Margin dimension labels */}
            <span style={{ ...marginLabelStyle, top: marginTop / 2 - 4, left: '50%', transform: 'translateX(-50%)' }}>
              {b.Margins?.Top ?? 1}"
            </span>
            <span style={{ ...marginLabelStyle, bottom: marginBottom / 2 - 4, left: '50%', transform: 'translateX(-50%)' }}>
              {b.Margins?.Bottom ?? 1}"
            </span>
            <span style={{ ...marginLabelStyle, left: marginLeft / 2 - 6, top: '50%', transform: 'translateY(-50%)' }}>
              {b.Margins?.Left ?? 1}"
            </span>
            <span style={{ ...marginLabelStyle, right: marginRight / 2 - 6, top: '50%', transform: 'translateY(-50%)' }}>
              {b.Margins?.Right ?? 1}"
            </span>
          </>
        )}

        {/* Header - three-column layout in top margin */}
        {hasHeaderContent && (
          <div
            className="absolute flex items-center justify-between"
            style={{
              top: inToPx(0.5),
              left: marginLeft,
              width: contentWidth,
            }}
          >
            {/* Left section */}
            <div className="flex items-center" style={{ minWidth: 0, flex: '1 1 0' }}>
              {headerLeftMode === 'logo' && getPositionImageUrl(headerLeftImageUrl) && (
                <img 
                  src={getPositionImageUrl(headerLeftImageUrl)} 
                  alt={t('Header left')} 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.HeaderLogoSize || 20)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {headerLeftMode === 'text' && headerTextLeft && (
                <span style={getHeaderStyle()}>{headerTextLeft}</span>
              )}
            </div>
            
            {/* Center section */}
            <div className="flex items-center justify-center" style={{ minWidth: 0, flex: '1 1 0' }}>
              {headerCenterMode === 'logo' && getPositionImageUrl(headerCenterImageUrl) && (
                <img 
                  src={getPositionImageUrl(headerCenterImageUrl)} 
                  alt={t('Header center')} 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.HeaderLogoSize || 20)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {headerCenterMode === 'text' && headerTextCenter && (
                <span style={getHeaderStyle()}>{headerTextCenter}</span>
              )}
            </div>
            
            {/* Right section */}
            <div className="flex items-center justify-end" style={{ minWidth: 0, flex: '1 1 0' }}>
              {headerRightMode === 'logo' && getPositionImageUrl(headerRightImageUrl) && (
                <img 
                  src={getPositionImageUrl(headerRightImageUrl)} 
                  alt={t('Header right')} 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.HeaderLogoSize || 20)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {headerRightMode === 'text' && headerTextRight && (
                <span style={getHeaderStyle()}>{headerTextRight}</span>
              )}
            </div>
          </div>
        )}

        {/* Footer - three-column layout in bottom margin */}
        {hasFooterContent && (
          <div
            className="absolute flex items-center justify-between"
            style={{
              bottom: inToPx(0.5),
              left: marginLeft,
              width: contentWidth,
            }}
          >
            {/* Left section */}
            <div className="flex items-center" style={{ minWidth: 0, flex: '1 1 0' }}>
              {footerLeftMode === 'logo' && getPositionImageUrl(footerLeftImageUrl) && (
                <img 
                  src={getPositionImageUrl(footerLeftImageUrl)} 
                  alt={t('Footer left')} 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.FooterLogoSize || 18)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {footerLeftMode === 'text' && footerTextLeft && (
                <span style={getFooterStyle()}>{footerTextLeft}</span>
              )}
            </div>
            
            {/* Center section */}
            <div className="flex items-center justify-center" style={{ minWidth: 0, flex: '1 1 0' }}>
              {footerCenterMode === 'logo' && getPositionImageUrl(footerCenterImageUrl) && (
                <img 
                  src={getPositionImageUrl(footerCenterImageUrl)} 
                  alt={t('Footer center')} 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.FooterLogoSize || 18)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {footerCenterMode === 'text' && footerTextCenter && (
                <span style={getFooterStyle()}>{footerTextCenter}</span>
              )}
            </div>
            
            {/* Right section */}
            <div className="flex items-center justify-end" style={{ minWidth: 0, flex: '1 1 0' }}>
              {footerRightMode === 'logo' && getPositionImageUrl(footerRightImageUrl) && (
                <img 
                  src={getPositionImageUrl(footerRightImageUrl)} 
                  alt={t('Footer right')} 
                  style={{ 
                    height: `${ptToPx(b.HeaderFooter?.FooterLogoSize || 18)}px`,
                    filter: grayscale,
                  }}
                />
              )}
              {footerRightMode === 'text' && footerTextRight && (
                <span style={getFooterStyle()}>{footerTextRight}</span>
              )}
            </div>
          </div>
        )}

        {/* Page number (live field) */}
        {pageNumEnabled && pageNumberText && (
          <div
            className="absolute flex"
            style={{
              [pnInHeader ? 'top' : 'bottom']: inToPx(0.5),
              left: marginLeft,
              width: contentWidth,
              justifyContent: pnJustify,
            }}
          >
            <span style={getFooterStyle()}>{pageNumberText}</span>
          </div>
        )}

        {/* Confidentiality notice - header / footer placements */}
        {confEnabled && (confPlacement === 'header' || confPlacement === 'footer') && (
          <div
            className="absolute flex justify-center"
            style={{
              [confPlacement === 'header' ? 'top' : 'bottom']: inToPx(0.3),
              left: marginLeft,
              width: contentWidth,
            }}
          >
            <span style={{ ...getFooterStyle(), fontWeight: 'bold', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {confText}
            </span>
          </div>
        )}

        {/* Content area */}
        <div
          className="absolute overflow-hidden"
          style={{
            top: marginTop,
            left: marginLeft,
            width: contentWidth,
            height: contentHeight,
            backgroundColor: showGuides ? 'rgba(248, 250, 252, 0.5)' : 'transparent',
          }}
        >
          {/* Letterhead (first page) */}
          {lhEnabled && (
            <div
              style={{
                borderBottom: '1px solid #d1d5db',
                paddingBottom: `${ptToPx(6)}px`,
                marginBottom: `${ptToPx(10)}px`,
                textAlign: 'center',
              }}
            >
              {b.Letterhead?.FirmName && (
                <div style={{ ...getLevelStyle('H2'), textAlign: 'center' }}>{b.Letterhead.FirmName}</div>
              )}
              {lhAddressLines.map((ln, i) => (
                <div key={i} style={{ ...getFooterStyle(), color: '#444' }}>{ln}</div>
              ))}
              {(b.Letterhead?.OrgNumber || b.Letterhead?.Contact) && (
                <div style={{ ...getFooterStyle(), color: '#444' }}>
                  {[b.Letterhead?.OrgNumber, b.Letterhead?.Contact].filter(Boolean).join('  ·  ')}
                </div>
              )}
            </div>
          )}

          {/* Confidentiality notice - top of first page */}
          {confEnabled && confPlacement === 'first-page' && pageNum === 1 && (
            <div
              style={{
                ...getFooterStyle(),
                fontWeight: 'bold',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: '#b91c1c',
                marginBottom: `${ptToPx(8)}px`,
              }}
            >
              {confText}
            </div>
          )}

          {children}
        </div>
        </div>
      </div>
    );
  };

  // Spacing helper - base tied to the configured paragraph "space after"
  const spaceAfterPt = Number(b.Typography?.ParagraphSpacingAfter);
  const spacingBasePt = Number.isFinite(spaceAfterPt) ? spaceAfterPt : 6;
  const spacing = (multiplier: number = 1) => ({ marginBottom: `${ptToPx(spacingBasePt) * multiplier}px` });

  const renderBlock = (blk: Block, i: number) => {
    if (blk.level === 'Body') {
      return <p key={i} style={{ ...getLevelStyle('Body'), ...spacing(1) }}>{blk.text}</p>;
    }
    const tag = blk.level === 'H1' ? 'h1' : blk.level === 'H2' ? 'h2' : blk.level === 'H3' ? 'h3' : 'h4';
    return React.createElement(
      tag,
      { key: i, style: { ...getLevelStyle(blk.level), ...spacing(1), marginTop: `${ptToPx(10)}px` } },
      blk.text,
    );
  };

  return (
    <div ref={containerRef} className="w-full">

      <div className="flex flex-col items-center gap-4 pb-4">
        {contentMode && (
          <>
            {/* Hidden full-flow measuring container at the true content width. */}
            <div
              ref={measureRef}
              aria-hidden
              style={{ position: 'absolute', left: -99999, top: 0, visibility: 'hidden', pointerEvents: 'none', width: `${contentWidth}px` }}
            >
              {blocks.map((blk, i) => renderBlock(blk, i))}
            </div>

            {blocks.length === 0 ? (
              <DocumentPage pageNum={1} totalPages={1}>
                <div style={{ padding: `${ptToPx(2)}px 0` }}>
                  <p style={{ ...getLevelStyle('Body') }}>{t('This document has no content yet.')}</p>
                </div>
              </DocumentPage>
            ) : (
              (pageSlices ?? [blocks.map((_, i) => i)]).map((slice, pi, arr) => (
                <div key={pi} className="flex flex-col items-center gap-1.5">
                  <DocumentPage pageNum={pi + 1} totalPages={arr.length}>
                    <div style={{ padding: `${ptToPx(2)}px 0` }}>
                      {slice.map((idx) => renderBlock(blocks[idx], idx))}
                    </div>
                  </DocumentPage>
                  <div className="text-[11px] font-medium text-fg-muted">
                    {t('Page {{n}} of {{total}}', { n: pi + 1, total: arr.length })}
                  </div>
                </div>
              ))
            )}
          </>
        )}
        {!contentMode && (
          <>
        {/* Main content page */}
        <DocumentPage pageNum={1}>
          <div style={{ padding: `${ptToPx(2)}px 0` }}>
            {/* Article Heading */}
            <h1 style={{ ...getLevelStyle('H1'), ...spacing(1.5) }}>
              Article I: Definitions
            </h1>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1.5) }}>
              For purposes of this Agreement, the following terms shall have the meanings set forth below. Unless otherwise specified, all references to sections shall refer to sections of this Agreement.
            </p>

            {/* Section */}
            <h2 style={{ ...getLevelStyle('H2'), ...spacing(1), marginTop: `${ptToPx(12)}px` }}>
              1.1 Key Terms
            </h2>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1.5) }}>
              "Confidential Information" means any non-public information disclosed by either party to the other, whether orally or in writing, that is designated as confidential or that reasonably should be understood to be confidential.
            </p>

            {/* Subsection */}
            <h3 style={{ ...getLevelStyle('H3'), ...spacing(1), marginTop: `${ptToPx(10)}px` }}>
              1.1.1 Definition Categories
            </h3>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1) }}>
              The following categories of information shall be deemed Confidential Information without requiring explicit designation: trade secrets, customer lists, pricing information, and proprietary methodologies.
            </p>

            {/* Sub-subsection */}
            <h4 style={{ ...getLevelStyle('H4'), ...spacing(0.5), marginTop: `${ptToPx(8)}px` }}>
              (a) Trade Secrets
            </h4>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1) }}>
              Information that derives independent economic value from not being generally known and is subject to reasonable efforts to maintain its secrecy.
            </p>

            <h4 style={{ ...getLevelStyle('H4'), ...spacing(0.5), marginTop: `${ptToPx(8)}px` }}>
              (b) Proprietary Data
            </h4>
            
            <p style={{ ...getLevelStyle('Body') }}>
              All business, technical, and financial information developed by or for the disclosing party that provides a competitive advantage in the marketplace.
            </p>
          </div>
        </DocumentPage>

        {/* Second page - shows continuation and footer working */}
        <DocumentPage pageNum={2}>
          <div style={{ padding: `${ptToPx(2)}px 0` }}>
            <h1 style={{ ...getLevelStyle('H1'), ...spacing(1.5) }}>
              Article II: Obligations
            </h1>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1.5) }}>
              Each party agrees to maintain the confidentiality of all Confidential Information received from the other party and to use such information solely for the purposes contemplated by this Agreement.
            </p>

            <h2 style={{ ...getLevelStyle('H2'), ...spacing(1), marginTop: `${ptToPx(12)}px` }}>
              2.1 Non-Disclosure
            </h2>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1.5) }}>
              Neither party shall disclose any Confidential Information to any third party without the prior written consent of the disclosing party, except as may be required by law or regulation.
            </p>

            <h2 style={{ ...getLevelStyle('H2'), ...spacing(1), marginTop: `${ptToPx(12)}px` }}>
              2.2 Permitted Disclosures
            </h2>
            
            <p style={{ ...getLevelStyle('Body'), ...spacing(1) }}>
              Notwithstanding the foregoing, either party may disclose Confidential Information to its employees, contractors, and advisors who have a need to know such information.
            </p>

            <h3 style={{ ...getLevelStyle('H3'), ...spacing(1), marginTop: `${ptToPx(10)}px` }}>
              2.2.1 Employee Obligations
            </h3>
            
            <p style={{ ...getLevelStyle('Body') }}>
              All such employees, contractors, and advisors must be bound by confidentiality obligations no less restrictive than those contained herein.
            </p>
          </div>
        </DocumentPage>
          </>
        )}
      </div>
    </div>
  );
};

export default BrandingPreview;
